<template>
  <SCASelect
    :class="$style.select"
    :label
    :items="items"
    :model-value="modelValue ?? undefined"
    :disabled="disabled"
    :test-id="testId"
    @update:model-value="onChange"
  >
    <template #input>
      <SCADatetimeInputInline
        ref="editor"
        :model-value="editorValue"
        :format="unit === 'hours' ? 'HH' : 'mm'"
        :disabled="disabled"
        @update:model-value="onEditorChange"
        @focusout="onEditorFocusOut"
        @next="emit('next')"
        @prev="emit('prev')"
      />
    </template>
  </SCASelect>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from "vue";

import SCASelect from "../SCASelect.vue";
import type { SelectableItem } from "../types";
import SCADatetimeInputInline from "./SCADatetimeInputInline.vue";

const props = withDefaults(
  defineProps<{
    modelValue: number | null;
    unit: "hours" | "minutes";
    step?: number;
    disabled?: boolean;
    testId?: string;
    label?: string;
    labelSize?: number;
    width?: number;
    gap?: number;
  }>(),
  {
    step: 1,
    disabled: false,
    testId: undefined,
    label: undefined,
    labelSize: 52,
    width: 136,
    gap: 12,
  },
);

const emit = defineEmits<{
  "update:modelValue": [value: number];
  next: [];
  prev: [];
}>();

const editor = ref<InstanceType<typeof SCADatetimeInputInline>>();

const items = computed<SelectableItem[]>(() => {
  const max = props.unit === "hours" ? 23 : 59;
  const out: SelectableItem[] = [];
  for (let i = 0; i <= max; i += props.step) {
    out.push({ id: i, label: String(i).padStart(2, "0") });
  }
  return out;
});

const editorValue = computed<Date | null>(() => {
  if (props.modelValue == null) return null;
  const date = new Date(2000, 0, 1);
  if (props.unit === "hours") date.setHours(props.modelValue);
  else date.setMinutes(props.modelValue);
  return date;
});

function onChange(id?: SelectableItem["id"]) {
  if (id === undefined) return;
  emit("update:modelValue", Number(id));
}

function onEditorChange(date: Date | null) {
  if (!date) return;
  emit("update:modelValue", props.unit === "hours" ? date.getHours() : date.getMinutes());
}

function onEditorFocusOut(event: FocusEvent) {
  const root = event.currentTarget as HTMLElement;
  if (event.relatedTarget instanceof Node && root.contains(event.relatedTarget)) return;
  void nextTick(() => editor.value?.reset());
}

defineExpose({
  focus: () => editor.value?.focus(),
  focusLast: () => editor.value?.focusLast(),
});
</script>

<style module lang="scss">
.select {
  width: v-bind("`${width}px`");
  --sca-label-size: v-bind("`${labelSize}px`");
  --space-component-gap-medium: v-bind("`${gap}px`");
}
</style>
